"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMarkupStore } from "../store";
import type { FeedbackItem } from "../types";
import { MessageIcon } from "./Icons";
import { useCurrentUrl } from "../hooks/useCurrentUrl";
import { isSamePage, matchesStatusFilter } from "../utils/helpers";
import {
    anchoredPinPoint,
    computePinPoint,
    canMeasureLayout,
    findPinElement,
    type PinPoint,
} from "../utils/pinPosition";

/** Frames an anchored pin must hold still before it stops being followed. */
const SETTLE_FRAMES = 10;

interface FeedbackPinsProps {
    onPinClick: (feedback: FeedbackItem) => void;
    /**
     * The window whose page the pins sit on. Defaults to this one; the device
     * preview passes its iframe's window and mounts the pins over the frame.
     */
    view?: Window;
    /** URL of the page in `view`. Defaults to tracking this window's URL. */
    url?: string;
}

export const FeedbackPins: React.FC<FeedbackPinsProps> = ({
    onPinClick,
    view: viewProp,
    url: urlProp,
}) => {
    const { feedbackItems, showPins, config, statusFilter, isPicking } =
        useMarkupStore();
    const containerRef = useRef<HTMLDivElement>(null);
    const isFramed = !!viewProp;

    // Bumped only when the page *layout* changes — never on scroll. Scrolling
    // doesn't move anything in page coordinates, so re-resolving pin positions
    // on scroll was pure waste: a React re-render plus a querySelector and a
    // forced layout per pin, on every one of the ~100 scroll events a second a
    // trackpad produces. That's what made the pins lag behind the page.
    const [layoutVersion, setLayoutVersion] = useState(0);

    useEffect(() => {
        const view = viewProp ?? window;
        const doc = view.document;
        let frame = 0;
        let lastWidth = doc.documentElement.scrollWidth;
        let lastHeight = doc.documentElement.scrollHeight;

        const measure = () => {
            frame = 0;
            const width = doc.documentElement.scrollWidth;
            const height = doc.documentElement.scrollHeight;
            // Ignore observer callbacks that don't actually change the page
            // box, otherwise any animation on the host page re-renders every
            // pin continuously.
            if (width === lastWidth && height === lastHeight) return;
            lastWidth = width;
            lastHeight = height;
            setLayoutVersion((v) => v + 1);
        };

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };

        // Uses the observed window's own ResizeObserver: one from this realm
        // doesn't reliably report on another document's elements.
        const Observer =
            (view as Window & { ResizeObserver?: typeof ResizeObserver })
                .ResizeObserver ?? ResizeObserver;
        const resizeObserver = new Observer(schedule);
        resizeObserver.observe(doc.documentElement);
        if (doc.body) resizeObserver.observe(doc.body);
        view.addEventListener("resize", schedule);

        // Images and webfonts settling are the main source of "the pin was in
        // the right place, then it moved" — catch them as they land.
        view.addEventListener("load", schedule);
        doc.addEventListener("readystatechange", schedule);

        // A new document (or preview frame) is a new layout, whatever its size.
        setLayoutVersion((v) => v + 1);

        return () => {
            resizeObserver.disconnect();
            view.removeEventListener("resize", schedule);
            view.removeEventListener("load", schedule);
            doc.removeEventListener("readystatechange", schedule);
            if (frame) cancelAnimationFrame(frame);
        };
    }, [viewProp]);

    // Filter pins for current page URL — currentUrl tracks SPA client-side
    // navigation (pushState/replaceState), not just full page loads, so pins
    // don't leak between routes or vanish after navigating without a reload.
    const pageUrl = useCurrentUrl();
    const currentUrl = urlProp ?? pageUrl;
    const pinMatch = config.pinMatch ?? "pathname";
    const currentPagePins = useMemo(() => {
        return feedbackItems.filter((item) => {
            if (!item.pinLocation) return false;
            if (!isSamePage(item.pageMetadata?.url, currentUrl, pinMatch))
                return false;
            // Pins mirror what the list is showing, so closed tickets stay off
            // the page until the list is filtered to Closed.
            if (!matchesStatusFilter(item.status, statusFilter)) return false;
            return true;
        });
    }, [feedbackItems, currentUrl, pinMatch, statusFilter]);

    // Page coordinates, recomputed only when the pins or the layout change.
    const pinPoints = useMemo(() => {
        const points = new Map<string, PinPoint>();

        // Server render: nothing to measure against, and a pin placed from
        // unmeasured coordinates would only jump once the real layout arrives.
        // `layoutVersion` re-runs this after mount, which is when the numbers
        // mean something.
        if (!canMeasureLayout()) return points;

        for (const item of currentPagePins) {
            if (item.pinLocation) {
                points.set(
                    item.id,
                    computePinPoint(item.pinLocation, viewProp),
                );
            }
        }
        return points;
        // layoutVersion is the signal to re-measure against the live DOM.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPagePins, layoutVersion, viewProp]);

    const hasPins = showPins && currentPagePins.length > 0;

    // Pin nodes by ticket id, so anchored pins can be moved without React.
    const pinNodes = useRef(new Map<string, HTMLDivElement>());

    // Scroll handling: the container moves by the scroll offset. One
    // transform write per animation frame, applied straight to the node so
    // React never re-renders while scrolling, and on the compositor so it
    // tracks the page smoothly instead of stepping a frame behind.
    //
    // Pins anchored to an element also follow that element as it's drawn.
    // Parallax and smooth-scroll libraries (GSAP ScrollSmoother, Lenis,
    // Locomotive) move content with transforms as the page scrolls, so an
    // element's page position isn't fixed: measured once, the pin sat over
    // whatever had moved under it. Smoothing also keeps the page easing for a
    // while after the last scroll event, so the anchors are followed every
    // frame until they've been still for a few frames.
    useEffect(() => {
        if (!hasPins) return;

        const view = viewProp ?? window;
        const doc = view.document;
        const anchored = currentPagePins.flatMap((item) => {
            const loc = item.pinLocation;
            if (!loc) return [];
            const el = findPinElement(loc, doc);
            return el ? [{ id: item.id, loc, el, left: NaN, top: NaN }] : [];
        });

        let frame = 0;
        let stillFrames = 0;
        const apply = () => {
            frame = 0;
            const el = containerRef.current;
            if (el) {
                el.style.transform = `translate3d(${-view.scrollX}px, ${-view.scrollY}px, 0)`;
            }

            // Measure every anchor before moving any pin, so the reads don't
            // force a layout per pin.
            const points = anchored.map((pin) => {
                // Re-rendered by the host app: find its replacement.
                if (!pin.el.isConnected) {
                    pin.el = findPinElement(pin.loc, doc) ?? pin.el;
                }
                return anchoredPinPoint(pin.loc, pin.el, view);
            });

            let moved = false;
            anchored.forEach((pin, i) => {
                const { left, top } = points[i];
                if (left === pin.left && top === pin.top) return;
                pin.left = left;
                pin.top = top;
                moved = true;
                const node = pinNodes.current.get(pin.id);
                if (node) {
                    node.style.left = `${left}px`;
                    node.style.top = `${top}px`;
                }
            });

            stillFrames = moved ? 0 : stillFrames + 1;
            if (anchored.length > 0 && stillFrames < SETTLE_FRAMES) schedule();
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(apply);
        };
        const wake = () => {
            stillFrames = 0;
            schedule();
        };

        apply();
        // Capture catches scrolling inside overflow containers too, which
        // never reaches the window.
        doc.addEventListener("scroll", wake, { capture: true, passive: true });
        view.addEventListener("scroll", wake, { passive: true });
        view.addEventListener("resize", wake, { passive: true });
        // Smooth-scroll libraries often cancel the native wheel and touch
        // scroll and drive the page themselves.
        view.addEventListener("wheel", wake, { passive: true });
        view.addEventListener("touchmove", wake, { passive: true });
        view.addEventListener("keydown", wake, { passive: true });
        // Anything that moves without input — sliders, reveal animations.
        const poll = anchored.length > 0 ? view.setInterval(wake, 500) : 0;

        return () => {
            doc.removeEventListener("scroll", wake, { capture: true });
            view.removeEventListener("scroll", wake);
            view.removeEventListener("resize", wake);
            view.removeEventListener("wheel", wake);
            view.removeEventListener("touchmove", wake);
            view.removeEventListener("keydown", wake);
            if (poll) view.clearInterval(poll);
            if (frame) cancelAnimationFrame(frame);
        };
        // pinPoints: a re-measure re-renders the pins' left/top, so start
        // again from what React wrote.
    }, [hasPins, viewProp, currentPagePins, pinPoints]);

    if (!hasPins) return null;

    // Anchored at the viewport origin and shifted by the scroll offset, so its
    // children can be laid out in plain page coordinates. position:fixed keeps
    // it independent of the host page's body margins and positioning, which a
    // position:absolute container would inherit. Over the device preview it's
    // absolute instead: the preview wraps it in a box exactly covering the
    // frame's viewport, which becomes the origin.
    const containerStyle: React.CSSProperties = {
        position: isFramed ? "absolute" : "fixed",
        top: 0,
        left: 0,
        width: 0,
        height: 0,
        overflow: "visible",
        pointerEvents: "none",
        zIndex: 999998,
        willChange: "transform",
    };

    return (
        <div
            ref={containerRef}
            className="feedback-pins-container"
            data-markup-pins
            style={containerStyle}
        >
            {currentPagePins.map((feedback) => {
                const point = pinPoints.get(feedback.id);
                if (!point) return null;

                const pinStyle: React.CSSProperties = {
                    position: "absolute",
                    left: point.left,
                    top: point.top,
                    transform: "translate(-50%, -50%)",
                    // Click-through while placing a pin or picking an
                    // element, so an existing pin can't swallow the click.
                    pointerEvents: isPicking ? "none" : "auto",
                    opacity: isPicking ? 0.35 : 1,
                };

                return (
                    <div
                        key={feedback.id}
                        ref={(node) => {
                            if (node) pinNodes.current.set(feedback.id, node);
                            else pinNodes.current.delete(feedback.id);
                        }}
                        className="feedback-pin-wrapper"
                        style={pinStyle}
                        onClick={() => onPinClick(feedback)}
                    >
                        <div className="feedback-pin group relative cursor-pointer">
                            {/* Pin marker */}
                            <div
                                className={`
                                w-8 h-8 rounded-full flex items-center justify-center
                                shadow-lg hover:shadow-xl transition-all
                                ${feedback.status === "open" ? "bg-matter-ink hover:bg-matter-ink/80" : "bg-green-500 hover:bg-green-600"}
                                border-2 border-white
                                hover:scale-110
                            `}
                            >
                                <MessageIcon className="w-4 h-4 text-white" />
                            </div>

                            {/* Hover tooltip */}
                            <div
                                className="
                                absolute bottom-full left-1/2 -translate-x-1/2 mb-2
                                opacity-0 group-hover:opacity-100
                                pointer-events-none
                                transition-opacity duration-200
                                bg-gray-900 text-white text-xs! rounded-lg py-2 px-3
                                whitespace-nowrap shadow-xl
                                max-w-[200px]
                            "
                            >
                                <div className="font-semibold truncate">
                                    {feedback.title || "Feedback"}
                                </div>
                                <div className="text-gray-300 text-[10px] mt-0.5">
                                    Click to view
                                </div>
                                {/* Arrow */}
                                <div
                                    className="
                                    absolute top-full left-1/2 -translate-x-1/2
                                    w-0 h-0
                                    border-l-4 border-l-transparent
                                    border-r-4 border-r-transparent
                                    border-t-4 border-t-gray-900
                                "
                                />
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FeedbackPins;
